// S311: e-postadaki bağlantının açtığı ekran (`/parola-belirle?t=...`). Hem davet
// (ilk parola) hem sıfırlama bu ekrandan geçer; fark yalnız başlıkta.
//
// 🔴 Kayıttan sonra OTOMATİK GİRİŞ YOK: kullanıcı yeni parolasını giriş ekranında bir
// kez yazar. Belirteç bir oturum anahtarı değil, tek kullanımlık bir yetki.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  PAROLA_KURALI,
  parolaApi,
  parolaKuralaUyar,
  sunucuHatasi,
  type BelirtecBilgisi,
} from '../api/parola';
import {
  GirisKabugu,
  ParolaAlani,
  girisAltLinkCls,
  girisButonCls,
  girisHataCls,
  girisMetinCls,
} from '../components/GirisKabugu';

const GECERSIZ_BAGLANTI = 'Bağlantı geçersiz ya da süresi dolmuş. Yeni bir bağlantı isteyin.';

/** Yalnız 400 bağlantının kendisini reddeder; 429 ve ağ hatası bağlantıyı bozuk göstermemeli. */
function kontrolHatasi(err: unknown): string {
  const { durum, mesaj } = sunucuHatasi(err);
  if (durum === 400) return mesaj ?? GECERSIZ_BAGLANTI;
  if (durum === 429) return 'Çok fazla deneme. Birkaç dakika sonra sayfayı yenileyin.';
  return 'Bağlantı şu an denetlenemedi. Birkaç dakika sonra sayfayı yenileyin.';
}

type Durum =
  | { tur: 'yukleniyor' }
  | { tur: 'gecersiz'; mesaj: string }
  | { tur: 'form'; bilgi: BelirtecBilgisi }
  | { tur: 'kaydedildi' };

export function ParolaBelirle() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const belirtec = params.get('t') ?? '';
  const [durum, setDurum] = useState<Durum>(() =>
    belirtec ? { tur: 'yukleniyor' } : { tur: 'gecersiz', mesaj: GECERSIZ_BAGLANTI },
  );
  const [parola, setParola] = useState('');
  const [tekrar, setTekrar] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!belirtec) return;
    let iptal = false;
    parolaApi
      .dogrula(belirtec)
      .then((bilgi) => {
        if (!iptal) setDurum({ tur: 'form', bilgi });
      })
      .catch((err) => {
        if (!iptal) setDurum({ tur: 'gecersiz', mesaj: kontrolHatasi(err) });
      });
    return () => {
      iptal = true;
    };
  }, [belirtec]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!parolaKuralaUyar(parola)) {
      setError(`Parola kurala uymuyor. ${PAROLA_KURALI}`);
      return;
    }
    if (parola !== tekrar) {
      setError('Parolalar aynı değil.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await parolaApi.belirle(belirtec, parola);
      setDurum({ tur: 'kaydedildi' });
    } catch (err) {
      const { durum: kod, mesaj } = sunucuHatasi(err);
      setError(
        kod === 429
          ? 'Çok fazla deneme. Birkaç dakika sonra tekrar deneyin.'
          : (mesaj ?? 'Bir sorun oluştu. Birkaç dakika sonra tekrar deneyin.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (durum.tur === 'yukleniyor') {
    return (
      <GirisKabugu baslik="Parola belirle">
        <p role="status" aria-busy="true" className={girisMetinCls}>
          Bağlantı denetleniyor…
        </p>
      </GirisKabugu>
    );
  }

  if (durum.tur === 'gecersiz') {
    return (
      <GirisKabugu baslik="Parola belirle">
        <p role="alert" className={girisHataCls}>
          {durum.mesaj}
        </p>
        <Link to="/sifremi-unuttum" className={girisAltLinkCls}>
          Yeni bağlantı iste
        </Link>
        <Link to="/login" className={girisAltLinkCls}>
          Girişe dön
        </Link>
      </GirisKabugu>
    );
  }

  if (durum.tur === 'kaydedildi') {
    return (
      <GirisKabugu baslik="Parola belirle">
        <p role="status" className={girisMetinCls}>
          Parolanız kaydedildi.
        </p>
        <button type="button" className={girisButonCls} onClick={() => navigate('/login', { replace: true })}>
          Giriş yap
        </button>
      </GirisKabugu>
    );
  }

  return (
    <GirisKabugu baslik={durum.bilgi.amac === 'davet' ? 'Parolanızı belirleyin' : 'Yeni parola'}>
      <form className="flex flex-col" onSubmit={handleSubmit} noValidate>
        <p className={girisMetinCls} data-testid="parola-eposta">
          {durum.bilgi.email}
        </p>
        {/* Parola yöneticileri yeni parolayı HANGİ hesaba kaydedeceğini bu gizli
            kullanıcı adı alanından okur. */}
        <input type="email" autoComplete="username" value={durum.bilgi.email} readOnly hidden />

        <ParolaAlani id="parola" etiket="Parola" deger={parola} degisti={setParola} />
        <ParolaAlani id="parola-tekrar" etiket="Parola tekrar" deger={tekrar} degisti={setTekrar} />
        <p className="m-0 -mt-1 mb-3 ml-0.5 text-[11px] leading-[1.5] text-[#6b6b6e]">{PAROLA_KURALI}</p>

        {error && (
          <p role="alert" className={girisHataCls}>
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting} className={girisButonCls}>
          {submitting ? 'Kaydediliyor…' : 'Parolayı kaydet'}
        </button>
      </form>
    </GirisKabugu>
  );
}
